import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

type Status = {
  network: 'threads' | 'facebook';
  status: 'processing' | 'completed' | 'failed';
  requestedAt: string;
  finishedAt: string | null;
};

const NETWORK = { threads: 'Threads', facebook: 'Facebook and Instagram' };
const day = (when: string | null) =>
  when ? new Date(when).toLocaleDateString([], { dateStyle: 'long' }) : '';

/**
 * The public page Meta sends someone to after asking for their data to be deleted
 * (apiserver reach/routes/metaCallbacks.js). Their code is all it knows them by.
 */
export default function DataDeletionStatus() {
  const { code = '' } = useParams();
  const [found, setFound] = useState<Status | null>(null);
  const [state, setState] = useState<'loading' | 'found' | 'unknown' | 'error'>('loading');

  useEffect(() => {
    let live = true;
    fetch(`${backendHost}/api/meta/deletion/${encodeURIComponent(code)}`)
      .then(async (res) => {
        if (!live) return;
        if (res.status === 404) return setState('unknown');
        if (!res.ok) return setState('error');
        setFound(await res.json());
        return setState('found');
      })
      .catch(() => live && setState('error'));
    return () => {
      live = false;
    };
  }, [code]);

  const from = found ? NETWORK[found.network] : '';
  const words = (() => {
    if (state === 'loading') return { title: 'Checking your request…', body: '' };
    if (state === 'unknown') {
      return {
        title: 'We can’t find that request',
        body: 'Check the address, or write to hello@tinycms.ai and we’ll look into it.',
      };
    }
    if (state === 'error' || !found) {
      return {
        title: 'We couldn’t check your request just now',
        body: 'Try again in a moment, or write to hello@tinycms.ai.',
      };
    }
    if (found.status === 'completed') {
      return {
        title: 'Your data has been deleted',
        body: `On ${day(found.finishedAt)} we deleted what tinycms held from ${from} for you: the account details, access tokens, post records and performance numbers.`,
      };
    }
    if (found.status === 'failed') {
      return {
        title: 'Your deletion isn’t finished yet',
        body: 'Something went wrong on our side and our team has been told. Write to hello@tinycms.ai if you’d like an update.',
      };
    }
    return {
      title: 'Your deletion is under way',
      body: `We received your request on ${day(found.requestedAt)}. Check back here shortly.`,
    };
  })();

  return (
    <main className="data-deletion">
      <h1>{words.title}</h1>
      {words.body ? <p>{words.body}</p> : null}
      <p className="data-deletion-code">Confirmation code: {code}</p>
    </main>
  );
}
